import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Award, Users, Zap } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { mockTestimonials } from '../mock';
import './HomePage.css';

const HomePage = () => {
  return (
    <div className="home-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <img 
          src="https://customer-assets.emergentagent.com/job_elite-boxing-camp/artifacts/qt97lboc_qq.jpeg"
          alt="Team Kent Boxing Training"
          className="hero-background"
        />
        <div className="hero-content">
          <h1 className="hero-title fade-in-up">
            TRAIN HARDER. <span className="hero-accent">LIVE STRONGER.</span> BOXING IS THE LIFESTYLE.
          </h1>
          <p className="hero-subtitle fade-in-up">
            Train like a champion at any level - beginners welcome.
          </p>
          <div className="hero-cta-group fade-in-up">
            <Link to="/booking" className="btn-primary">
              Book Your Session
              <ArrowRight size={20} style={{ marginLeft: '8px' }} />
            </Link>
            <Link to="/programs" className="btn-secondary">
              View Programs
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section about-section">
        <div className="about-content">
          <div className="about-text">
            <h2 className="section-title">ABOUT TEAM KENT BOXING INTERNATIONAL GYM</h2>
            <p>
              We're not just a gym—we're a community dedicated to excellence. Our camp combines world-class coaching, 
              state-of-the-art facilities, and a proven training methodology that has produced champions at every level.
            </p>
            <p>
              Whether you're stepping into the ring for the first time or preparing for your next title fight, 
              our experienced coaches will guide you every step of the way with personalized attention and expert technique.
            </p>
            <div className="about-stats">
              <div className="stat-item">
                <h3>15+</h3>
                <p>Years Experience</p>
              </div>
              <div className="stat-item">
                <h3>150+</h3>
                <p>Fitness Enthusiasts Trained</p>
              </div>
              <div className="stat-item">
                <h3>20+</h3>
                <p>Kiddies Trained</p>
              </div>
            </div>
          </div>
          <div className="about-image">
            <img 
              src="https://customer-assets.emergentagent.com/job_elite-boxing-camp/artifacts/h95k9yrq_b.jpeg"
              alt="Team Kent Boxing Gym"
            />
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section section-dark why-section">
        <h2 className="section-title text-center">WHY CHOOSE US</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">
              <Award size={40} />
            </div>
            <h3>Expert Coaching</h3>
            <p>
              Train with former champions and certified professionals who have decades of combined experience.
            </p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <Target size={40} />
            </div>
            <h3>Personalized Programs</h3>
            <p>
              Every fighter is unique. We create customized training plans tailored to your goals and skill level.
            </p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <Zap size={40} />
            </div>
            <h3>State-of-the-Art Facility</h3>
            <p>
              Professional-grade equipment and a motivating environment designed for peak performance.
            </p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <Users size={40} />
            </div>
            <h3>Strong Community</h3>
            <p>
              Join a supportive community of fighters who push each other to be better every single day.
            </p>
          </div>
        </div>
      </section>

      {/* Programs Preview */}
      <section className="section programs-preview">
        <h2 className="section-title text-center">OUR STUDIO SESSIONS</h2>
        <p className="section-subtitle text-center">
          Private studio training for 45 minutes to 1 hour per session
        </p>
        <div className="programs-grid">
          <div className="program-card">
            <div className="program-badge">PERSONALIZED</div>
            <h3>1-on-1 Private Session</h3>
            <p>Personalized one-on-one training with dedicated coach attention for maximum results.</p>
            <div className="program-details">
              <span className="program-duration">45 min - 1 hour</span>
              <span className="program-price">R450/session</span>
            </div>
            <ul className="program-features">
              <li>Full coach attention</li>
              <li>Customized workout plan</li>
              <li>Immediate feedback</li>
              <li>Flexible scheduling</li>
            </ul>
          </div>

          <div className="program-card">
            <div className="program-badge">PARTNER</div>
            <h3>2-on-1 Private Session</h3>
            <p>Train with a partner and share the experience while getting professional coaching.</p>
            <div className="program-details">
              <span className="program-duration">45 min - 1 hour</span>
              <span className="program-price">R350/person</span>
            </div>
            <ul className="program-features">
              <li>Bring a friend or family</li>
              <li>Shared motivation</li>
              <li>Partner drills</li>
              <li>Cost-effective training</li>
            </ul>
          </div>

          <div className="program-card">
            <div className="program-badge">SMALL GROUP</div>
            <h3>3-on-1 Private Session</h3>
            <p>Small group training for maximum results with individual attention from your coach.</p>
            <div className="program-details">
              <span className="program-duration">45 min - 1 hour</span>
              <span className="program-price">R300/person</span>
            </div>
            <ul className="program-features">
              <li>Small group dynamics</li>
              <li>Individual attention</li>
              <li>Team motivation</li>
              <li>Great value</li>
            </ul>
          </div>

          <div className="program-card">
            <div className="program-badge">BEST VALUE</div>
            <h3>Semi-Private Small Groups</h3>
            <p>Train in a supportive small group environment with all fitness levels welcome.</p>
            <div className="program-details">
              <span className="program-duration">45 min - 1 hour</span>
              <span className="program-price">R250/person</span>
            </div>
            <ul className="program-features">
              <li>Community atmosphere</li>
              <li>Affordable option</li>
              <li>Group energy</li>
              <li>All fitness levels</li>
            </ul>
          </div>
        </div>
        <div className="text-center" style={{ marginTop: '3rem' }}>
          <Link to="/programs" className="btn-primary">
            View All Services
          </Link>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section section-dark testimonials-section">
        <h2 className="section-title text-center">WHAT OUR FITNESS ENTHUSIASTS SAY</h2>
        <div className="testimonials-grid">
          {mockTestimonials.map((testimonial) => (
            <div key={testimonial.id} className="testimonial-card">
              <div className="testimonial-stars">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <span key={i}>★</span>
                ))}
              </div>
              <p className="testimonial-text">"{testimonial.text}"</p>
              <div className="testimonial-author">
                <strong>{testimonial.name}</strong>
                <span>{testimonial.role}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>READY TO START YOUR JOURNEY?</h2>
          <p>Join the elite. Transform your fighting skills and reach your full potential.</p>
          <Link to="/booking" className="btn-primary btn-large">
            Book Your First Session
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;